/**
 * Terrain heights for the 3D world (docs/archive/3D.md, phase 2): ground height, terraces, stairs, cliffs,
 * water and decks, per tile, built from a zone's map plus its hand-authored spec
 * (data/heights/<zone>.ts). The renderer maps tile (x, y) to world (x, heightAt(x, y), y).
 *
 * Two kinds of map:
 * - a map with a level layer (an authored layout, docs/archive/YANYU.md §9): the rules already stand on its
 *   levels (`levelAt`), so the field takes them as they are, flights and walls included; the spec
 *   (`LevelHeightSpec`) only adds what is to look at: the sea's shelf, the depth of ponds and
 *   brooks, shore kinds, names;
 * - a generated map: the field is raised from the map and a `HeightSpec` (rolling ground, pads,
 *   grades, paddies, piers, brooks, flights), for the look only.
 *
 * Units: one tile is one metre across (`TILE_METRES`); heights are metres above the sea's surface.
 * Rule heights (terraces, boulders, roofs, jumps) are world pixels: `PX_METRES` converts them, the
 * same scale as the `?camera=free` prototype (render/world3d).
 *
 * The field is per tile, not a coarser grid: terraces, stairs, piers, paddies and building plinths
 * all line up with tiles, and walls between them must fall on tile edges. Each tile has a centre
 * height (`ground`), a kind for each of its edges, and four corner heights. `heightAt` is bilinear
 * over a tile's corners; a corner is shared (averaged) only by the tiles joined to each other
 * across smooth edges (flat, slope, sandy or reedy shores, and up and down a flight), so slopes are
 * continuous and walls (steps, cliffs, stone banks, deck sides, stair cheeks) stay sharp. Inside a
 * flight of stairs or a ramp the height is the flight's straight incline.
 */
import { clamp, valueNoise } from './rng';
import {
  flightIdAt,
  hasLevels,
  isBorder,
  levelCm,
  madeAt,
  ownerAt,
  surfKind,
  variantAt,
  isSolid,
  PX_METRES,
  STEP_UP,
  Surface,
  standAt,
  surfaceAt,
  terraceOf,
  Tile,
  tileAt,
  wallBetween,
  type Facing,
  type GameMap,
  type MapFlight,
  type Region,
} from './map';
import { flightLevel } from './layout';
import type { ZoneId } from './data/zones';
import { YANYU_HEIGHTS } from './data/heights/yanyu';

/** One tile is a metre across. */
export const TILE_METRES = 1;
/** Rule heights are world pixels (`PX_METRES` each); the highest step walked without stairs (`MAX_STEP`). */
export { MAX_STEP, PX_METRES } from './map';

export const EDGE_KINDS = ['flat', 'slope', 'step', 'cliff', 'stairs', 'ramp', 'shore', 'falls'] as const;
/**
 * What lies between two tiles. flat: level ground; slope: ground rising smoothly; step: a built
 * edge (a retaining wall, a terrace's wall, a plinth, a curb, a bund, a stair's cheek, a deck's
 * side); cliff: a natural rock face; stairs and ramp: up or down a flight; shore: water meeting
 * land; falls: water dropping to lower water.
 */
export type EdgeKind = (typeof EDGE_KINDS)[number];
export const SHORE_KINDS = ['sand', 'stone', 'reeds', 'earth'] as const;
/** The bank of a water tile that touches land: a sandy beach, a stone bank or quay, reeds, or a paddy's earth bund. */
export type ShoreKind = (typeof SHORE_KINDS)[number];
export const WATER_KINDS = ['sea', 'pond', 'pool', 'brook', 'paddy'] as const;
export type WaterKind = (typeof WATER_KINDS)[number];
export const DECK_KINDS = ['pier', 'bridge'] as const;
export type DeckKind = (typeof DECK_KINDS)[number];

/** A flight of stairs or a ramp: a straight incline over a rectangle of tiles. */
export interface StairFlight {
  readonly kind: 'stairs' | 'ramp';
  /** Tiles [i0, i1) × [j0, j1). */
  readonly rect: Region;
  /** The way down: the foot is on this side of the rectangle, the head on the opposite side. */
  readonly down: Facing;
  /** Height at the foot edge and at the head edge (metres). */
  readonly bottom: number;
  readonly top: number;
  /** Steps to draw (stairs); 0 for a ramp. */
  readonly steps: number;
  readonly name?: string;
  /** A flight along a winding path (a map's level layer): its incline runs along the path. */
  readonly path?: MapFlight['path'];
}

/** A level area: a plaza, a yard, a courtyard. Water inside it is left alone. */
export interface Pad {
  readonly name: string;
  readonly rect?: Region;
  readonly circle?: { readonly x: number; readonly y: number; readonly r: number };
  readonly level: number;
  /** Blends into the ground round it (no curb): a yard of packed earth rather than paving. */
  readonly soft?: boolean;
}

/** A strip graded along one axis: the height runs straight between the given points. */
export interface Grade {
  readonly name: string;
  readonly rect: Region;
  readonly axis: 'x' | 'y';
  /** [coordinate along the axis (tiles), height], in order along the axis. */
  readonly points: readonly (readonly [number, number])[];
}

/** A brook along a path of [x, y, water surface]: `width` tiles wide, its bed `depth` under the surface. */
export interface Brook {
  readonly name: string;
  readonly path: readonly (readonly [x: number, y: number, surface: number])[];
  readonly width: number;
  readonly depth: number;
  readonly shore: ShoreKind;
}

/** An inland pool of the map's water tiles, picked by a point inside it. */
export interface PondSpec {
  readonly name: string;
  readonly at: readonly [number, number];
  readonly kind: 'pond' | 'pool';
  /** Its surface lies this far under its lowest bank. */
  readonly drop: number;
  /** Depth in the middle (the margins are half as deep). */
  readonly depth: number;
  readonly shore: ShoreKind;
  /** Banks of another kind within these rectangles. */
  readonly shores?: readonly { readonly rect: Region; readonly shore: ShoreKind }[];
}

/**
 * How to raise a zone's terrain: plain data, read by `buildHeights`. Everything else (the sea,
 * the beach, terraces, building plinths, mountains) is derived from the map.
 */
export interface HeightSpec {
  readonly zone: ZoneId;
  /** The rolling land: heights every `step` tiles, rows[gy][gx] at (gx·step, gy·step), bilinear between. */
  readonly grid: { readonly step: number; readonly rows: readonly (readonly number[])[] };
  /** Undulation added to natural ground (± metres), smoothed. */
  readonly noise: number;
  /** Most a tile of natural ground may differ from a walkable neighbour (under MAX_STEP). */
  readonly grade: number;
  readonly sea: {
    /** The bed at the waterline, and how much deeper each tile out, down to `deepest`. */
    readonly shelf: number;
    readonly drop: number;
    readonly deepest: number;
    /** Bed at the foot of a stone shore. */
    readonly stoneShelf: number;
    /** Land beside the sea: `beachTop` at the waterline, `beachRise` higher each tile inland. */
    readonly beachTop: number;
    readonly beachRise: number;
    /** Shore kinds other than sand (stone by rock comes by itself). */
    readonly shores: readonly { readonly rect: Region; readonly shore: ShoreKind }[];
  };
  readonly pads: readonly Pad[];
  /**
   * The level at the foot of a terrace (its courtyard), by the structure's id or "i,j"; the
   * platform stands TERRACE.height higher. Without one, the ground at its middle.
   */
  readonly terraces?: Readonly<Record<string, number>>;
  readonly grades: readonly Grade[];
  /** Rice terraces: rows [j0, j1) of paddies at `level` (the bunds' top), the water this shallow. */
  readonly paddies?: {
    readonly bands: readonly (readonly [j0: number, j1: number, level: number])[];
    readonly bed: number;
    readonly water: number;
  };
  readonly piers?: {
    readonly level: number;
    /** Lower landing stages (where boats tie up). */
    readonly stages: readonly { readonly rect: Region; readonly level: number }[];
  };
  readonly bridges: readonly { readonly name: string; readonly rect: Region; readonly level: number }[];
  readonly brooks: readonly Brook[];
  readonly ponds: readonly PondSpec[];
  /** Flights authored by hand (the terraces' own stairs come from the map). */
  readonly flights: readonly StairFlight[];
  /** Built footprints stand on a stone plinth this high above the ground round them. */
  readonly plinth: number;
  readonly mountains: {
    /** First rock tile above its foot, and how much higher each tile further in (near the foot). */
    readonly cliff: number;
    readonly rise: number;
    /** Rock rises in ledges this tall (stacked cliffs with flat mossy tops). */
    readonly band: number;
    /**
     * The peaks: deep in a range the rock levels off at `max` above its foot, less by up to this
     * fraction where the ridge noise is low (so the skyline has peaks and saddles).
     */
    readonly ridge: number;
    readonly max: number;
    /** Rock beside a brook stands at least this far over its water. */
    readonly bank: number;
  };
}

/** The terrain of one zone's map. Plain typed arrays, one value per tile (index j·w + i). */
export interface HeightField {
  readonly zone: ZoneId;
  readonly w: number;
  readonly h: number;
  /** Centre height of what is stood on (a deck's top over water, the bed under open water). */
  readonly ground: Float32Array;
  /** Four corner heights per tile, in the order nw, ne, sw, se. */
  readonly corners: Float32Array;
  /** Water surface, NaN where dry. */
  readonly water: Float32Array;
  /** The bed under the water, NaN where dry. */
  readonly bed: Float32Array;
  /** 1 + index into WATER_KINDS, 0 where dry. */
  readonly waterKind: Uint8Array;
  /** 1 + index into SHORE_KINDS for water touching land, else 0. */
  readonly shore: Uint8Array;
  /** 1 + index into DECK_KINDS, else 0. */
  readonly deck: Uint8Array;
  /** Index into EDGE_KINDS of each tile's east edge (to i+1) and south edge (to j+1). */
  readonly edgeE: Uint8Array;
  readonly edgeS: Uint8Array;
  /** 1 + index into `flights`, else 0. */
  readonly flight: Uint16Array;
  readonly flights: readonly StairFlight[];
  readonly bridges: readonly { readonly name: string; readonly rect: Region; readonly level: number }[];
}

/**
 * What a map with a level layer adds to its levels, to look at (the rules never read it): the
 * sea's bed, the depth of its ponds and brooks, which banks are sand, stone or reeds, the names of
 * its bridges and piers, and how high rock stands over a brook beside it.
 */
export interface LevelHeightSpec {
  readonly zone: ZoneId;
  readonly sea: {
    /** The bed at the waterline, and how much deeper each tile out, down to `deepest`. */
    readonly shelf: number;
    readonly drop: number;
    readonly deepest: number;
    /** Bed at the foot of a stone shore. */
    readonly stoneShelf: number;
    /** Shore kinds other than the one the land beside gives (sand; stone by rock or a quay). */
    readonly shores: readonly { readonly rect: Region; readonly shore: ShoreKind }[];
  };
  /** Inland pools (picked by a point inside): pond or pool, depth in the middle, banks. */
  readonly ponds: readonly Omit<PondSpec, 'drop'>[];
  /** Brooks (picked by a point on them): their depth and banks. Others: 0.5 deep, stone banks. */
  readonly brooks: readonly {
    readonly name: string;
    readonly at: readonly [number, number];
    readonly depth: number;
    readonly shore: ShoreKind;
  }[];
  /** Decks over inland water, named by a point on them (piers over the sea are found by themselves). */
  readonly bridges: readonly { readonly name: string; readonly at: readonly [number, number] }[];
  readonly piers: readonly { readonly name: string; readonly at: readonly [number, number] }[];
  /** Paddies: the water this far under the lowest dry bank round it, the bed this far under that. */
  readonly paddy: { readonly under: number; readonly bed: number };
  /** Rock beside a brook stands at least this far over its water. */
  readonly bank: number;
}

/** Hand-authored specs by zone; generated zones without one get flat ground (sea, rock and terraces only). */
export const HEIGHT_SPECS: Partial<Record<ZoneId, HeightSpec>> = {};
/** Specs for the zones whose map has a level layer (an authored layout). */
export const LEVEL_SPECS: Partial<Record<ZoneId, LevelHeightSpec>> = { yanyu: YANYU_HEIGHTS };

const LEVEL_DEFAULT = (zone: ZoneId): LevelHeightSpec => ({
  zone,
  sea: { shelf: -0.3, drop: 0.45, deepest: -6, stoneShelf: -1.2, shores: [] },
  ponds: [],
  brooks: [],
  bridges: [],
  piers: [],
  paddy: { under: 0.05, bed: 0.12 },
  bank: 1.2,
});

const FLAT_SPEC = (zone: ZoneId): HeightSpec => ({
  zone,
  grid: {
    step: 1 << 10,
    rows: [
      [0, 0],
      [0, 0],
    ],
  },
  noise: 0,
  grade: 0.3,
  sea: { shelf: -0.3, drop: 0.45, deepest: -6, stoneShelf: -1.2, beachTop: 0.35, beachRise: 0.2, shores: [] },
  pads: [],
  grades: [],
  bridges: [],
  brooks: [],
  ponds: [],
  flights: [],
  plinth: 0.3,
  mountains: { cliff: 3, rise: 2, band: 1.6, ridge: 0.5, max: 20, bank: 1.2 },
});

const cache = new WeakMap<GameMap, HeightField>();

/** The terrain of a map (built once per map, then cached). */
export function heightsOf(m: GameMap): HeightField {
  let f = cache.get(m);
  if (!f) {
    f = hasLevels(m)
      ? buildLevelHeights(m, LEVEL_SPECS[m.zone] ?? LEVEL_DEFAULT(m.zone))
      : buildHeights(m, HEIGHT_SPECS[m.zone] ?? FLAT_SPEC(m.zone));
    cache.set(m, f);
  }
  return f;
}

const fieldOf = (z: HeightField | GameMap): HeightField => ('ground' in z ? z : heightsOf(z));

const STEP: Readonly<Record<Facing, readonly [number, number]>> = {
  n: [0, -1],
  e: [1, 0],
  s: [0, 1],
  w: [-1, 0],
};

/** Height along a flight at a point (straight from its foot to its head, or along its path). */
export function flightHeight(f: StairFlight, x: number, y: number): number {
  if (f.path) return flightLevel(f as MapFlight, x, y);
  const [i0, i1, j0, j1] = f.rect;
  const t =
    f.down === 'e'
      ? (i1 - x) / (i1 - i0)
      : f.down === 'w'
        ? (x - i0) / (i1 - i0)
        : f.down === 's'
          ? (j1 - y) / (j1 - j0)
          : (y - j0) / (j1 - j0);
  return f.bottom + (f.top - f.bottom) * clamp(t, 0, 1);
}

const inRect = (r: Region, i: number, j: number) => i >= r[0] && i < r[1] && j >= r[2] && j < r[3];

/** The flights of a map's terraces (their stairs, from `TERRACE` and each structure's spans). */
export function terraceFlights(m: GameMap, padOf: (st: number) => number): StairFlight[] {
  const out: StairFlight[] = [];
  m.structures.forEach((st, k) => {
    if (st.kind !== 'terrace') return;
    const T = terraceOf(st);
    const pad = padOf(k);
    const f = st.facing ?? 's';
    for (const [s0, s1] of st.stairs ?? []) {
      const rect: Region =
        f === 'e'
          ? [st.i + st.w - T.run, st.i + st.w, st.j + s0, st.j + s1]
          : f === 'w'
            ? [st.i, st.i + T.run, st.j + s0, st.j + s1]
            : f === 's'
              ? [st.i + s0, st.i + s1, st.j + st.h - T.run, st.j + st.h]
              : [st.i + s0, st.i + s1, st.j, st.j + T.run];
      out.push({
        kind: 'stairs',
        rect,
        down: f,
        bottom: pad,
        top: pad + T.height * PX_METRES,
        steps: T.steps,
        name: `${st.id ?? 'terrace'} stair ${s0}`,
      });
    }
  });
  return out;
}

const BUILT = new Set(['house', 'inn', 'pagoda', 'pavilion', 'gate', 'moongate']);

/** Piecewise-straight interpolation through [coordinate, value] points. */
function along(points: readonly (readonly [number, number])[], c: number): number {
  if (c <= points[0]![0]) return points[0]![1];
  for (let k = 1; k < points.length; k++) {
    const [a, ha] = points[k - 1]!;
    const [b, hb] = points[k]!;
    if (c <= b) return ha + ((hb - ha) * (c - a)) / (b - a);
  }
  return points[points.length - 1]![1];
}

/** Builds a map's height field from its spec. Pure and deterministic. */
export function buildHeights(m: GameMap, spec: HeightSpec): HeightField {
  const { w, h } = m;
  const N = w * h;
  const at = (i: number, j: number) => j * w + i;
  const inside = (i: number, j: number) => i >= 0 && j >= 0 && i < w && j < h;
  const g = new Float64Array(N);
  const water = new Float32Array(N).fill(NaN);
  const bed = new Float32Array(N).fill(NaN);
  const waterKind = new Uint8Array(N);
  const shore = new Uint8Array(N);
  const deck = new Uint8Array(N);
  /** Which built or levelled part a tile belongs to (0: natural ground); walls run between parts. */
  const part = new Int32Array(N);
  let parts = 0;
  const lock = new Uint8Array(N);
  const tile = (n: number) => tileAt(m, n % w, (n / w) | 0);
  const vr = (n: number) => variantAt(m, n % w, (n / w) | 0);
  const ow = (n: number) => ownerAt(m, n % w, (n / w) | 0);
  const mountain = new Uint8Array(N);
  for (let n = 0; n < N; n++) {
    const i = n % w;
    const j = (n / w) | 0;
    if (tile(n) === Tile.Rock && (vr(n) >= 4 || isBorder(m, i, j))) mountain[n] = 1;
  }
  const W = (k: WaterKind) => WATER_KINDS.indexOf(k) + 1;
  const S = (k: ShoreKind) => SHORE_KINDS.indexOf(k) + 1;
  const N4 = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const;

  // 1. The rolling land: the grid, bilinear, with a little smoothed undulation.
  const { step, rows } = spec.grid;
  const gridAt = (x: number, y: number) => {
    const gx = clamp(x / step, 0, rows[0]!.length - 1);
    const gy = clamp(y / step, 0, rows.length - 1);
    const x0 = Math.min(Math.floor(gx), rows[0]!.length - 2);
    const y0 = Math.min(Math.floor(gy), rows.length - 2);
    const u = gx - x0;
    const v = gy - y0;
    const r0 = rows[y0]!;
    const r1 = rows[y0 + 1]!;
    return (r0[x0]! * (1 - u) + r0[x0 + 1]! * u) * (1 - v) + (r1[x0]! * (1 - u) + r1[x0 + 1]! * u) * v;
  };
  for (let j = 0; j < h; j++)
    for (let i = 0; i < w; i++)
      g[at(i, j)] =
        gridAt(i + 0.5, j + 0.5) +
        spec.noise * (valueNoise(i / 5 + 3.3, j / 5 + 7.1) * 2 - 1) +
        spec.noise * 0.5 * (valueNoise(i / 2 + 11.7, j / 2 + 1.9) * 2 - 1);

  // 2. Water: the sea is the water that reaches the map's top edge; other pools are ponds. Pier
  //    planks over the water count as sea underneath.
  const isPierOverWater = (i: number, j: number) => {
    let wet = 0;
    for (const [di, dj] of N4) {
      let a = i + di;
      let b = j + dj;
      while (inside(a, b) && tileAt(m, a, b) === Tile.Pier) {
        a += di;
        b += dj;
      }
      if (inside(a, b) && tileAt(m, a, b) === Tile.Water) wet++;
    }
    return wet >= 2;
  };
  const wetTile = new Uint8Array(N);
  for (let n = 0; n < N; n++) {
    if (tile(n) === Tile.Water) wetTile[n] = 1;
    else if (tile(n) === Tile.Pier) {
      deck[n] = DECK_KINDS.indexOf('pier') + 1;
      if (isPierOverWater(n % w, (n / w) | 0)) wetTile[n] = 1;
    }
  }
  const comp = new Int32Array(N).fill(-1);
  const comps: number[][] = [];
  for (let n = 0; n < N; n++) {
    if (!wetTile[n] || comp[n]! >= 0) continue;
    const list = [n];
    comp[n] = comps.length;
    for (let k = 0; k < list.length; k++) {
      const c = list[k]!;
      for (const [di, dj] of N4) {
        const a = (c % w) + di;
        const b = ((c / w) | 0) + dj;
        if (!inside(a, b)) continue;
        const q = at(a, b);
        if (wetTile[q] && comp[q]! < 0) {
          comp[q] = comps.length;
          list.push(q);
        }
      }
    }
    comps.push(list);
  }
  const isSea = (n: number) => comp[n]! >= 0 && comps[comp[n]!]!.some((q) => q < w);
  const sea = new Uint8Array(N);
  for (let n = 0; n < N; n++) if (wetTile[n] && isSea(n)) sea[n] = 1;

  // 3. Natural ground: smoothed twice, then the beach brought down to the sea.
  for (let pass = 0; pass < 2; pass++) {
    const src = g.slice();
    for (let j = 0; j < h; j++)
      for (let i = 0; i < w; i++) {
        let s = 0;
        let c = 0;
        for (let dj = -1; dj <= 1; dj++)
          for (let di = -1; di <= 1; di++)
            if (inside(i + di, j + dj)) {
              s += src[at(i + di, j + dj)]!;
              c++;
            }
        g[at(i, j)] = s / c;
      }
  }
  const toSea = distance(
    w,
    h,
    (n) => !!sea[n],
    (n) => !sea[n] && !mountain[n],
  );
  for (let n = 0; n < N; n++) {
    const d = toSea[n]!;
    if (!sea[n] && d > 0 && d < 12) g[n] = Math.min(g[n]!, spec.sea.beachTop + spec.sea.beachRise * (d - 1));
  }

  // 4. Levelled parts: pads, terraces, grades, paddies, decks, flights.
  const setPart = (n: number, p: number, level: number) => {
    g[n] = level;
    part[n] = p;
    lock[n] = 1;
  };
  const soft = new Set<number>();
  for (const pad of spec.pads) {
    const p = ++parts;
    if (pad.soft) soft.add(p);
    for (let j = 0; j < h; j++)
      for (let i = 0; i < w; i++) {
        const n = at(i, j);
        if (wetTile[n] || mountain[n]) continue;
        const c = pad.circle;
        const hit = pad.rect ? inRect(pad.rect, i, j) : !!c && Math.hypot(i + 0.5 - c.x, j + 0.5 - c.y) < c.r;
        if (hit) setPart(n, p, pad.level);
      }
  }
  // Terraces: the platform up top, its stairs, and the paving round it level at its foot.
  const terracePad: number[] = [];
  m.structures.forEach((st, k) => {
    if (st.kind !== 'terrace') return;
    const centre = at(st.i + (st.w >> 1), st.j + (st.h >> 1));
    const level = spec.terraces?.[st.id ?? `${st.i},${st.j}`] ?? Math.round(g[centre]! * 20) / 20;
    terracePad[k] = level;
    const courtyard = ++parts;
    const top = ++parts;
    // The footprint and the paving joined to it.
    const seen = new Set<number>();
    const list: number[] = [];
    for (let j = st.j; j < st.j + st.h; j++) for (let i = st.i; i < st.i + st.w; i++) list.push(at(i, j));
    list.forEach((n) => seen.add(n));
    for (let q = 0; q < list.length; q++) {
      const n = list[q]!;
      for (const [di, dj] of N4) {
        const a = (n % w) + di;
        const b = ((n / w) | 0) + dj;
        if (!inside(a, b)) continue;
        const r = at(a, b);
        if (!seen.has(r) && tile(r) === Tile.Paved && vr(r) === st.variant) {
          seen.add(r);
          list.push(r);
        }
      }
    }
    for (const n of list) {
      const own = ow(n) === k + 1 && tile(n) === Tile.Terrace;
      setPart(n, own ? top : courtyard, own ? level + terraceOf(st).height * PX_METRES : level);
    }
  });
  for (const gr of spec.grades) {
    // A graded way is cut into the ground, not built up: it meets the ground round it smoothly.
    const p = ++parts;
    soft.add(p);
    for (let j = gr.rect[2]; j < gr.rect[3]; j++)
      for (let i = gr.rect[0]; i < gr.rect[1]; i++) {
        const n = at(i, j);
        if (wetTile[n] || mountain[n]) continue;
        setPart(n, p, along(gr.points, gr.axis === 'x' ? i + 0.5 : j + 0.5));
      }
  }
  if (spec.paddies) {
    const { bands } = spec.paddies;
    const bandOf = (j: number) => bands.findIndex(([j0, j1]) => j >= j0 && j < j1);
    const bandPart = bands.map(() => ++parts);
    const bundPart = bands.map(() => ++parts);
    for (let j = 0; j < h; j++)
      for (let i = 0; i < w; i++) {
        const n = at(i, j);
        const b = bandOf(j);
        if (tile(n) === Tile.Paddy && b >= 0) {
          const level = bands[b]![2];
          setPart(n, bandPart[b]!, level - spec.paddies.bed);
          bed[n] = level - spec.paddies.bed;
          water[n] = level - spec.paddies.bed + spec.paddies.water;
          waterKind[n] = W('paddy');
          continue;
        }
        if (wetTile[n] || mountain[n] || tile(n) === Tile.Paddy) continue;
        // A bund: dry ground beside a paddy, as high as the highest paddy terrace it holds.
        let best = -1;
        for (let dj = -1; dj <= 1; dj++)
          for (let di = -1; di <= 1; di++) {
            if (!inside(i + di, j + dj) || tileAt(m, i + di, j + dj) !== Tile.Paddy) continue;
            const bb = bandOf(j + dj);
            if (bb >= 0 && (best < 0 || bands[bb]![2] > bands[best]![2])) best = bb;
          }
        if (best >= 0) setPart(n, bundPart[best]!, bands[best]![2]);
      }
    for (let n = 0; n < N; n++)
      if (waterKind[n] === W('paddy'))
        for (const [di, dj] of N4) {
          const a = (n % w) + di;
          const b = ((n / w) | 0) + dj;
          if (inside(a, b) && waterKind[at(a, b)] !== W('paddy')) shore[n] = S('earth');
        }
  }
  if (spec.piers) {
    const p = ++parts;
    const stage = spec.piers.stages.map(() => ++parts);
    for (let n = 0; n < N; n++) {
      if (tile(n) !== Tile.Pier) continue;
      const k = spec.piers.stages.findIndex((s) => inRect(s.rect, n % w, (n / w) | 0));
      setPart(n, k >= 0 ? stage[k]! : p, k >= 0 ? spec.piers.stages[k]!.level : spec.piers.level);
    }
  }
  for (const br of spec.bridges) {
    const p = ++parts;
    for (let j = br.rect[2]; j < br.rect[3]; j++)
      for (let i = br.rect[0]; i < br.rect[1]; i++) {
        setPart(at(i, j), p, br.level);
        deck[at(i, j)] = DECK_KINDS.indexOf('bridge') + 1;
      }
  }
  const flights = [...terraceFlights(m, (k) => terracePad[k]!), ...spec.flights];
  const flight = new Uint16Array(N);
  flights.forEach((f, k) => {
    const p = ++parts;
    for (let j = f.rect[2]; j < f.rect[3]; j++)
      for (let i = f.rect[0]; i < f.rect[1]; i++) {
        const n = at(i, j);
        flight[n] = k + 1;
        setPart(n, p, flightHeight(f, i + 0.5, j + 0.5));
      }
  });

  // 5. Natural ground graded so that no walkable neighbours differ by more than `grade` (levelled
  //    parts hold; the ground round them gives way). Walls the rules already keep walkers off (a
  //    terrace's wall) are left alone, and so are steps along a flight.
  const isRunEdge = (a: number, b: number) => {
    const fa = flight[a]! ? flights[flight[a]! - 1]! : null;
    const fb = flight[b]! ? flights[flight[b]! - 1]! : null;
    const run = (f: StairFlight) => {
      const ax = f.down === 'e' || f.down === 'w';
      return ax ? ((a / w) | 0) === ((b / w) | 0) : a % w === b % w;
    };
    return (!!fa && run(fa)) || (!!fb && run(fb));
  };
  const lvl = (n: number) => surfaceAt(m, (n % w) + 0.5, ((n / w) | 0) + 0.5) ?? 0;
  const walk = new Uint8Array(N);
  for (let n = 0; n < N; n++) walk[n] = isSolid(m, n % w, (n / w) | 0) ? 0 : 1;
  const land = (n: number) => !wetTile[n] && !mountain[n] && waterKind[n] !== W('paddy');
  const pairs: number[] = [];
  for (let j = 0; j < h; j++)
    for (let i = 0; i < w; i++) {
      const a = at(i, j);
      for (const b of [i + 1 < w ? a + 1 : -1, j + 1 < h ? a + w : -1]) {
        if (b < 0) continue;
        const both = (land(a) || walk[a]) && (land(b) || walk[b]);
        if (!both || isRunEdge(a, b)) continue;
        if (walk[a] && walk[b] && Math.abs(lvl(a) - lvl(b)) > STEP_UP) continue;
        if (lock[a] && lock[b]) continue;
        pairs.push(a, b);
      }
    }
  const L = spec.grade;
  for (let pass = 0; pass < 400; pass++) {
    let moved = false;
    for (let k = 0; k < pairs.length; k += 2) {
      const a = pairs[k]!;
      const b = pairs[k + 1]!;
      const d = g[a]! - g[b]!;
      if (Math.abs(d) <= L + 1e-9) continue;
      moved = true;
      const [hi, lo] = d > 0 ? [a, b] : [b, a];
      if (lock[hi]) g[lo] = g[hi]! - L;
      else if (lock[lo]) g[hi] = g[lo]! + L;
      else {
        const mid = (g[hi]! + g[lo]!) / 2;
        g[hi] = mid + L / 2;
        g[lo] = mid - L / 2;
      }
    }
    if (!moved) break;
  }

  // 6. Buildings stand on plinths a little above the highest ground round them.
  m.structures.forEach((st, k) => {
    if (!BUILT.has(st.kind)) return;
    const own: number[] = [];
    for (let n = 0; n < N; n++) if (ow(n) === k + 1 && tile(n) === Tile.Building) own.push(n);
    if (!own.length) return;
    let top = -Infinity;
    for (const n of own)
      for (let dj = -1; dj <= 1; dj++)
        for (let di = -1; di <= 1; di++) {
          const a = (n % w) + di;
          const b = ((n / w) | 0) + dj;
          if (!inside(a, b)) continue;
          const q = at(a, b);
          if (ow(q) !== k + 1 && land(q)) top = Math.max(top, g[q]!);
        }
    const p = ++parts;
    for (const n of own) setPart(n, p, (Number.isFinite(top) ? top : g[n]!) + spec.plinth);
  });

  // 7. Ponds and pools: level a little under their lowest bank.
  comps.forEach((list, c) => {
    if (list.some((n) => sea[n])) return;
    const ps = spec.ponds.find((p) => comp[at(Math.floor(p.at[0]), Math.floor(p.at[1]))] === c);
    const kind = ps?.kind ?? 'pond';
    let low = Infinity;
    for (const n of list)
      for (const [di, dj] of N4) {
        const a = (n % w) + di;
        const b = ((n / w) | 0) + dj;
        if (inside(a, b) && land(at(a, b))) low = Math.min(low, g[at(a, b)]!);
      }
    if (!Number.isFinite(low)) low = 0;
    const surface = low - (ps?.drop ?? 0.3);
    const depth = ps?.depth ?? 0.8;
    const inner = distance(
      w,
      h,
      (n) => comp[n] !== c,
      (n) => comp[n] === c,
    );
    for (const n of list) {
      const d = inner[n]!;
      water[n] = surface;
      bed[n] = surface - depth * Math.min(1, 0.5 + 0.25 * (d - 1));
      g[n] = bed[n]!;
      waterKind[n] = W(kind);
      if (touchesLand(n)) {
        const o = ps?.shores?.find((s) => inRect(s.rect, n % w, (n / w) | 0));
        shore[n] = S(o?.shore ?? ps?.shore ?? 'stone');
      }
    }
  });
  function touchesLand(n: number): boolean {
    for (const [di, dj] of N4) {
      const a = (n % w) + di;
      const b = ((n / w) | 0) + dj;
      if (inside(a, b) && !wetTile[at(a, b)] && !(waterKind[at(a, b)] === W('brook'))) return true;
    }
    return false;
  }

  // 8. The sea: level 0, its bed shelving away from the land.
  const toLand = distance(
    w,
    h,
    (n) => !sea[n],
    (n) => !!sea[n],
  );
  for (let n = 0; n < N; n++) {
    if (!sea[n]) continue;
    const i = n % w;
    const j = (n / w) | 0;
    let rocky = false;
    let landBeside = false;
    for (const [di, dj] of N4) {
      if (!inside(i + di, j + dj)) continue;
      const q = at(i + di, j + dj);
      if (sea[q]) continue;
      landBeside = true;
      if (mountain[q]) rocky = true;
    }
    const d = toLand[n]!;
    const sh = spec.sea;
    water[n] = 0;
    waterKind[n] = W('sea');
    bed[n] = Math.max(sh.deepest, (rocky ? sh.stoneShelf : sh.shelf) - sh.drop * (d - 1));
    if (!deck[n]) g[n] = bed[n]!;
    if (landBeside && !deck[n]) {
      const o = sh.shores.find((s) => inRect(s.rect, i, j));
      shore[n] = S(o ? o.shore : rocky ? 'stone' : 'sand');
    }
  }

  // 9. Brooks: down channels cut through the rock (and under bridges).
  for (const br of spec.brooks) {
    const P = br.path;
    for (let j = 0; j < h; j++)
      for (let i = 0; i < w; i++) {
        const x = i + 0.5;
        const y = j + 0.5;
        let best = Infinity;
        let surf = 0;
        for (let k = 1; k < P.length; k++) {
          const [ax, ay, as] = P[k - 1]!;
          const [bx, by, bs] = P[k]!;
          const dx = bx - ax;
          const dy = by - ay;
          const t = clamp(((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy), 0, 1);
          const d = Math.hypot(x - ax - dx * t, y - ay - dy * t);
          if (d < best - 1e-9) {
            best = d;
            surf = as + (bs - as) * t;
          }
        }
        if (best >= br.width / 2) continue;
        const n = at(i, j);
        if (wetTile[n] && !deck[n]) continue;
        water[n] = Math.round(surf * 20) / 20;
        bed[n] = water[n]! - br.depth;
        waterKind[n] = W('brook');
        mountain[n] = 0;
        if (!deck[n]) g[n] = bed[n]!;
      }
    for (let n = 0; n < N; n++) {
      if (waterKind[n] !== W('brook')) continue;
      for (const [di, dj] of N4) {
        const a = (n % w) + di;
        const b = ((n / w) | 0) + dj;
        if (inside(a, b) && !(waterKind[at(a, b)]! > 0) && !deck[n]) shore[n] = S(br.shore);
      }
    }
  }

  // 10. Mountains: rock rises in ledges with distance from the open ground (or water) at its foot.
  const M = spec.mountains;
  const depth = new Int32Array(N).fill(-1);
  const foot = new Float64Array(N);
  let layer: number[] = [];
  for (let n = 0; n < N; n++)
    if (!mountain[n]) {
      depth[n] = 0;
      foot[n] = Number.isNaN(water[n]!) ? g[n]! : Math.max(g[n]!, water[n]!);
      layer.push(n);
    }
  for (let d = 1; layer.length; d++) {
    const next: number[] = [];
    for (const n of layer)
      for (const [di, dj] of N4) {
        const a = (n % w) + di;
        const b = ((n / w) | 0) + dj;
        if (!inside(a, b)) continue;
        const q = at(a, b);
        if (depth[q] === -1) {
          depth[q] = d;
          foot[q] = foot[n]!;
          next.push(q);
        } else if (depth[q] === d) foot[q] = Math.max(foot[q]!, foot[n]!);
      }
    layer = next;
  }
  for (let n = 0; n < N; n++) {
    if (!mountain[n]) continue;
    const i = n % w;
    const j = (n / w) | 0;
    const d = Math.max(1, depth[n]!);
    // Rising `rise` a tile from the cliff at the foot, levelling off toward this range's peak here.
    const peak = M.max * (1 - M.ridge + M.ridge * valueNoise(i / 9 + 5.5, j / 9 + 2.5));
    const span = Math.max(1, peak - M.cliff);
    const raw = M.cliff + span * (1 - Math.exp((-M.rise * (d - 1)) / span));
    const jitter = (valueNoise(i / 2.3 + 9.1, j / 2.3 + 4.4) - 0.5) * M.band;
    g[n] = foot[n]! + clamp(Math.ceil((raw + jitter) / M.band) * M.band, M.cliff, M.max);
    part[n] = -1;
  }
  for (let n = 0; n < N; n++) {
    if (waterKind[n] !== W('brook')) continue;
    for (const [di, dj] of N4) {
      const a = (n % w) + di;
      const b = ((n / w) | 0) + dj;
      if (inside(a, b) && mountain[at(a, b)]) g[at(a, b)] = Math.max(g[at(a, b)]!, water[n]! + M.bank);
    }
  }

  // 11. Edges, then corners.
  const ground = Float32Array.from(g);
  const isOpenWater = (n: number) => !Number.isNaN(water[n]!) && !deck[n];
  const E = (k: EdgeKind) => EDGE_KINDS.indexOf(k);
  const classify = (a: number, b: number): EdgeKind => {
    if (isRunEdge(a, b)) return flights[(flight[a] || flight[b])! - 1]!.kind;
    const wa = isOpenWater(a);
    const wb = isOpenWater(b);
    if (wa && wb) return Math.abs(water[a]! - water[b]!) >= 0.2 ? 'falls' : 'flat';
    if (wa || wb) return deck[wa ? b : a] ? 'step' : 'shore';
    const dh = Math.abs(ground[a]! - ground[b]!);
    if (flight[a] || flight[b]) return dh < 0.05 ? 'flat' : 'step';
    if (mountain[a] || mountain[b]) return dh < 0.05 ? 'flat' : dh <= 1 ? 'slope' : 'cliff';
    const hard = (p: number) => p !== 0 && !soft.has(p);
    if (part[a] !== part[b] && dh >= 0.05 && (hard(part[a]!) || hard(part[b]!))) return 'step';
    return dh < 0.05 ? 'flat' : 'slope';
  };
  const edgeE = new Uint8Array(N);
  const edgeS = new Uint8Array(N);
  for (let j = 0; j < h; j++)
    for (let i = 0; i < w; i++) {
      const n = at(i, j);
      if (i + 1 < w) edgeE[n] = E(classify(n, n + 1));
      if (j + 1 < h) edgeS[n] = E(classify(n, n + w));
    }
  const corners = cornersOf(w, h, ground, edgeE, edgeS, flight, flights, shore, isOpenWater);

  return {
    zone: m.zone,
    w,
    h,
    ground,
    corners,
    water,
    bed,
    waterKind,
    shore,
    deck,
    edgeE,
    edgeS,
    flight,
    flights,
    bridges: spec.bridges,
  };
}

/**
 * The height field of a map with a level layer (docs/archive/YANYU.md §6): the ground is the rules' own
 * level on every tile (a flight's incline on its stairs and ramps), so what is seen is what is
 * stood on, walls between levels included; the spec adds what is only to look at: the beds under
 * the water, the kinds of banks, the names of bridges. Pure and deterministic.
 */
export function buildLevelHeights(m: GameMap, spec: LevelHeightSpec): HeightField {
  const { w, h } = m;
  const N = w * h;
  const at = (i: number, j: number) => j * w + i;
  const inside = (i: number, j: number) => i >= 0 && j >= 0 && i < w && j < h;
  const tile = (n: number) => tileAt(m, n % w, (n / w) | 0);
  const vr = (n: number) => variantAt(m, n % w, (n / w) | 0);
  const lv = (n: number) => levelCm(m, n % w, (n / w) | 0) / 100;
  const N4 = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const;
  const near = (n: number) => {
    const out: number[] = [];
    const i = n % w;
    const j = (n / w) | 0;
    for (const [di, dj] of N4) if (inside(i + di, j + dj)) out.push(at(i + di, j + dj));
    return out;
  };
  const W = (k: WaterKind) => WATER_KINDS.indexOf(k) + 1;
  const S = (k: ShoreKind) => SHORE_KINDS.indexOf(k) + 1;
  const g = new Float32Array(N);
  const water = new Float32Array(N).fill(NaN);
  const bed = new Float32Array(N).fill(NaN);
  const waterKind = new Uint8Array(N);
  const shore = new Uint8Array(N);
  const deck = new Uint8Array(N);
  const mountain = (n: number) => tile(n) === Tile.Rock && vr(n) >= 4;
  const wet = (n: number) => tile(n) === Tile.Water;
  /** The map's water by its variant: 0 the sea, 1 a pond, 2 a brook, 3 a pond of lotus. */
  const seaTile = (n: number) => wet(n) && vr(n) === 0;
  const flights: StairFlight[] = [...(m.flights ?? [])];
  const flight = Uint16Array.from({ length: N }, (_, n) => flightIdAt(m, n % w, (n / w) | 0));
  for (let n = 0; n < N; n++)
    g[n] = flight[n] ? flightHeight(flights[flight[n]! - 1]!, (n % w) + 0.5, ((n / w) | 0) + 0.5) : lv(n);

  // 1. Water: the sea shelving away from the land, ponds deepest in their middle, brooks.
  const toLand = distance(
    w,
    h,
    (n) => !wet(n) && tile(n) !== Tile.Pier,
    (n) => seaTile(n) || tile(n) === Tile.Pier,
  );
  const comp = new Int32Array(N).fill(-1);
  const comps: number[][] = [];
  for (let n = 0; n < N; n++) {
    if (!wet(n) || seaTile(n) || comp[n]! >= 0) continue;
    const list = [n];
    comp[n] = comps.length;
    for (let k = 0; k < list.length; k++)
      for (const q of near(list[k]!))
        if (wet(q) && !seaTile(q) && comp[q]! < 0 && (vr(q) === 2) === (vr(n) === 2)) {
          comp[q] = comps.length;
          list.push(q);
        }
    comps.push(list);
  }
  for (let n = 0; n < N; n++) {
    if (!seaTile(n)) continue;
    const rocky = near(n).some((q) => mountain(q));
    const sh = spec.sea;
    water[n] = lv(n);
    waterKind[n] = W('sea');
    bed[n] = Math.max(
      sh.deepest,
      (rocky ? sh.stoneShelf : sh.shelf) - sh.drop * (Math.max(1, toLand[n]!) - 1),
    );
  }
  comps.forEach((list, c) => {
    const n0 = list[0]!;
    const brook = vr(n0) === 2;
    const contains = (p: readonly [number, number]) => comp[at(Math.floor(p[0]), Math.floor(p[1]))] === c;
    const ps = brook ? undefined : spec.ponds.find((p) => contains(p.at));
    const bs = brook ? spec.brooks.find((b) => contains(b.at)) : undefined;
    const inner = distance(
      w,
      h,
      (q) => comp[q] !== c,
      (q) => comp[q] === c,
    );
    for (const n of list) {
      water[n] = lv(n);
      waterKind[n] = W(brook ? 'brook' : (ps?.kind ?? 'pond'));
      const depth = brook
        ? (bs?.depth ?? 0.5)
        : (ps?.depth ?? 0.8) * Math.min(1, 0.5 + 0.25 * (inner[n]! - 1));
      bed[n] = water[n]! - depth;
    }
  });

  // 2. Decks: a pier over the sea, a bridge over inland water, or a platform over dry ground.
  const deckComp = new Int32Array(N).fill(-1);
  const decks: { tiles: number[]; kind: DeckKind | null }[] = [];
  for (let n = 0; n < N; n++) {
    if (tile(n) !== Tile.Pier || deckComp[n]! >= 0) continue;
    const list = [n];
    deckComp[n] = decks.length;
    let sea = false;
    let inland = false;
    for (let k = 0; k < list.length; k++)
      for (const q of near(list[k]!)) {
        if (seaTile(q)) sea = true;
        else if (wet(q)) inland = true;
        if (tile(q) === Tile.Pier && deckComp[q]! < 0) {
          deckComp[q] = decks.length;
          list.push(q);
        }
      }
    decks.push({ tiles: list, kind: sea ? 'pier' : inland ? 'bridge' : null });
  }
  for (const d of decks) {
    if (!d.kind) continue;
    for (const n of d.tiles) {
      deck[n] = DECK_KINDS.indexOf(d.kind) + 1;
      // Over the water when water lies beyond it, through the deck, on two sides or more.
      const i = n % w;
      const j = (n / w) | 0;
      const hits: number[] = [];
      for (const [di, dj] of N4) {
        let a = i + di;
        let b = j + dj;
        while (inside(a, b) && tile(at(a, b)) === Tile.Pier) {
          a += di;
          b += dj;
        }
        if (inside(a, b) && wet(at(a, b))) hits.push(at(a, b));
      }
      if (hits.length < 2) continue;
      const q = hits.reduce((x, y) => (water[y]! < water[x]! ? y : x));
      water[n] = water[q]!;
      bed[n] = bed[q]!;
      waterKind[n] = waterKind[q]!;
    }
  }

  // 3. Paddies: water a little under the lowest dry bank round each, its bed under that.
  for (let n = 0; n < N; n++) {
    if (tile(n) !== Tile.Paddy) continue;
    let low = lv(n);
    let edge = false;
    for (const q of near(n))
      if (tile(q) !== Tile.Paddy) {
        edge = true;
        if (!wet(q)) low = Math.min(low, g[q]! - spec.paddy.under);
      }
    water[n] = low;
    bed[n] = low - spec.paddy.bed;
    waterKind[n] = W('paddy');
    if (edge) shore[n] = S('earth');
  }

  // 4. Banks: sand, stone (rock, a quay, a wall), reeds, as the land beside and the spec say.
  const isOpenWater = (n: number) => !Number.isNaN(water[n]!) && !deck[n];
  for (let n = 0; n < N; n++) {
    if (!wet(n)) continue;
    const land = near(n).filter((q) => !wet(q));
    if (!land.length) continue;
    const i = n % w;
    const j = (n / w) | 0;
    const stone = land.some(
      (q) => mountain(q) || [Tile.Paved, Tile.Wall, Tile.Building, Tile.Pier].includes(tile(q) as 8),
    );
    const reeds = land.some((q) => surfKind(m, q % w, (q / w) | 0) === Surface.Moss);
    let k: ShoreKind;
    if (seaTile(n))
      k = spec.sea.shores.find((s) => inRect(s.rect, i, j))?.shore ?? (stone ? 'stone' : 'sand');
    else if (vr(n) === 2)
      k =
        spec.brooks.find((b) => comp[at(Math.floor(b.at[0]), Math.floor(b.at[1]))] === comp[n])?.shore ??
        'stone';
    else {
      const ps = spec.ponds.find((p) => comp[at(Math.floor(p.at[0]), Math.floor(p.at[1]))] === comp[n]);
      k = ps?.shores?.find((s) => inRect(s.rect, i, j))?.shore ?? (reeds ? 'reeds' : (ps?.shore ?? 'stone'));
    }
    if (reeds && !stone) k = 'reeds';
    shore[n] = S(k);
  }

  // 5. The ground under open water is its bed; rock over a brook stands clear of it.
  for (let n = 0; n < N; n++) if (isOpenWater(n)) g[n] = bed[n]!;
  for (let n = 0; n < N; n++) {
    if (waterKind[n] !== W('brook')) continue;
    for (const q of near(n)) if (mountain(q)) g[q] = Math.max(g[q]!, water[n]! + spec.bank);
  }

  // 6. Edges, then corners.
  const ground = g;
  const E = (k: EdgeKind) => EDGE_KINDS.indexOf(k);
  const walkable = (n: number) => !isSolid(m, n % w, (n / w) | 0);
  const runs = (f: StairFlight, a: number, b: number) =>
    !!f.path || (f.down === 'e' || f.down === 'w') === (((a / w) | 0) === ((b / w) | 0));
  const classify = (a: number, b: number): EdgeKind => {
    const ia = a % w;
    const ja = (a / w) | 0;
    const ib = b % w;
    const jb = (b / w) | 0;
    const wall = wallBetween(m, ia, ja, ib, jb);
    const fa = flight[a] ? flights[flight[a]! - 1]! : null;
    const fb = flight[b] ? flights[flight[b]! - 1]! : null;
    if (!wall && ((fa && runs(fa, a, b)) || (fb && runs(fb, a, b)))) return (fa ?? fb)!.kind;
    const wa = isOpenWater(a);
    const wb = isOpenWater(b);
    if (wa && wb) return Math.abs(water[a]! - water[b]!) >= 0.2 ? 'falls' : 'flat';
    if (wa || wb) return deck[wa ? b : a] ? 'step' : 'shore';
    const dh = Math.abs(ground[a]! - ground[b]!);
    if (dh < 0.05) return 'flat';
    if (mountain(a) || mountain(b)) return dh <= 1 ? 'slope' : 'cliff';
    if (wall || !walkable(a) || !walkable(b) || fa || fb) return 'step';
    return madeAt(m, a % w, (a / w) | 0) === 2 || madeAt(m, b % w, (b / w) | 0) === 2 ? 'step' : 'slope';
  };
  const edgeE = new Uint8Array(N);
  const edgeS = new Uint8Array(N);
  for (let j = 0; j < h; j++)
    for (let i = 0; i < w; i++) {
      const n = at(i, j);
      if (i + 1 < w) edgeE[n] = E(classify(n, n + 1));
      if (j + 1 < h) edgeS[n] = E(classify(n, n + w));
    }
  const corners = cornersOf(w, h, ground, edgeE, edgeS, flight, flights, shore, isOpenWater);

  // Named bridges: the deck each spec point stands on, as a rectangle, at its highest.
  const bridges = spec.bridges.flatMap((b) => {
    const d = decks[deckComp[at(Math.floor(b.at[0]), Math.floor(b.at[1]))]!];
    if (!d) return [];
    const xs = d.tiles.map((n) => n % w);
    const ys = d.tiles.map((n) => (n / w) | 0);
    const rect: Region = [Math.min(...xs), Math.max(...xs) + 1, Math.min(...ys), Math.max(...ys) + 1];
    return [{ name: b.name, rect, level: Math.max(...d.tiles.map((n) => g[n]!)) }];
  });
  return {
    zone: m.zone,
    w,
    h,
    ground,
    corners,
    water,
    bed,
    waterKind,
    shore,
    deck,
    edgeE,
    edgeS,
    flight,
    flights,
    bridges,
  };
}

/** The highest drop from a sandy or reedy shore's land into its water's bed that is smoothed over (metres). */
const SHORE_JOIN = 1.5;

/**
 * Corner heights from tile heights and edge kinds: a corner is the mean of the tiles round it
 * joined to each other across smooth edges (flat, slope, a sandy or reedy shore, up a flight), a
 * flight's own incline on and beside it; walls (steps, cliffs, banks, cheeks) stay sharp.
 */
function cornersOf(
  w: number,
  h: number,
  ground: Float32Array,
  edgeE: Uint8Array,
  edgeS: Uint8Array,
  flight: Uint16Array,
  flights: readonly StairFlight[],
  shore: Uint8Array,
  isOpenWater: (n: number) => boolean,
): Float32Array {
  const at = (i: number, j: number) => j * w + i;
  const inside = (i: number, j: number) => i >= 0 && j >= 0 && i < w && j < h;
  const joins = (a: number, b: number): boolean => {
    const k =
      EDGE_KINDS[a + 1 === b ? edgeE[a]! : a - 1 === b ? edgeE[b]! : a + w === b ? edgeS[a]! : edgeS[b]!];
    if (k === 'stairs' || k === 'ramp') return true;
    if (flight[a] || flight[b]) return false;
    if (k === 'flat' || k === 'slope') return true;
    if (k === 'shore') {
      const s = SHORE_KINDS[shore[isOpenWater(a) ? a : b]! - 1];
      // A beach runs gently into the water; a wall's top or a terrace by it stays sharp.
      return (s === 'sand' || s === 'reeds') && Math.abs(ground[a]! - ground[b]!) <= SHORE_JOIN;
    }
    return false;
  };
  const corners = new Float32Array(w * h * 4);
  for (let j = 0; j < h; j++)
    for (let i = 0; i < w; i++) {
      const n = at(i, j);
      for (let c = 0; c < 4; c++) {
        const sx = c & 1 ? 1 : -1;
        const sy = c & 2 ? 1 : -1;
        const cx = i + (c & 1);
        const cy = j + (c & 2 ? 1 : 0);
        const own = flight[n] ? flights[flight[n]! - 1]! : null;
        if (own) {
          corners[n * 4 + c] = flightHeight(own, cx, cy);
          continue;
        }
        // The 2×2 block round the corner: this tile, its neighbours across x and y, the diagonal.
        // Joined to this tile: across x (h), across y (v), and the diagonal through either.
        const hn = inside(i + sx, j) ? at(i + sx, j) : -1;
        const vn = inside(i, j + sy) ? at(i, j + sy) : -1;
        const dn = inside(i + sx, j + sy) ? at(i + sx, j + sy) : -1;
        let inH = hn >= 0 && joins(n, hn);
        let inV = vn >= 0 && joins(n, vn);
        const inD = dn >= 0 && ((inH && joins(hn, dn)) || (inV && joins(vn, dn)));
        if (inD) {
          inH ||= joins(dn, hn);
          inV ||= joins(dn, vn);
        }
        const members = [n, inH ? hn : -1, inV ? vn : -1, inD ? dn : -1];
        let fl = -1;
        let s = 0;
        let count = 0;
        for (const q of members) {
          if (q < 0) continue;
          if (flight[q] && fl < 0) fl = q;
          s += ground[q]!;
          count++;
        }
        corners[n * 4 + c] = fl >= 0 ? flightHeight(flights[flight[fl]! - 1]!, cx, cy) : s / count;
      }
    }
  return corners;
}

/** Tile-steps (4-connected) from the tiles `from` picks, through the tiles `through` picks; 0 at the sources, -1 unreached. */
function distance(
  w: number,
  h: number,
  from: (n: number) => boolean,
  through: (n: number) => boolean,
): Int32Array {
  const N = w * h;
  const d = new Int32Array(N).fill(-1);
  let layer: number[] = [];
  for (let n = 0; n < N; n++)
    if (from(n)) {
      d[n] = 0;
      layer.push(n);
    }
  for (let k = 1; layer.length; k++) {
    const next: number[] = [];
    for (const n of layer) {
      const i = n % w;
      const j = (n / w) | 0;
      for (const q of [
        i + 1 < w ? n + 1 : -1,
        i > 0 ? n - 1 : -1,
        j + 1 < h ? n + w : -1,
        j > 0 ? n - w : -1,
      ])
        if (q >= 0 && d[q] === -1 && through(q)) {
          d[q] = k;
          next.push(q);
        }
    }
    layer = next;
  }
  return d;
}

// ── Queries ─────────────────────────────────────────────────────────────────────────────────────

const tileIndex = (f: HeightField, x: number, y: number) => {
  const i = clamp(Math.floor(x), 0, f.w - 1);
  const j = clamp(Math.floor(y), 0, f.h - 1);
  return [i, j, j * f.w + i] as const;
};

/** Height of the terrain at a point (metres): bilinear over the tile's corners, straight up a flight. */
export function heightAt(zone: HeightField | GameMap, x: number, y: number): number {
  const f = fieldOf(zone);
  const [i, j, n] = tileIndex(f, x, y);
  const u = clamp(x - i, 0, 1);
  const v = clamp(y - j, 0, 1);
  const c = f.corners;
  const k = n * 4;
  return (c[k]! * (1 - u) + c[k + 1]! * u) * (1 - v) + (c[k + 2]! * (1 - u) + c[k + 3]! * u) * v;
}

/** The terrain's unit normal at a point: components along map x, map y, and up. */
export function normalAt(
  zone: HeightField | GameMap,
  x: number,
  y: number,
): { x: number; y: number; up: number } {
  const f = fieldOf(zone);
  const [i, j, n] = tileIndex(f, x, y);
  const u = clamp(x - i, 0, 1);
  const v = clamp(y - j, 0, 1);
  const c = f.corners;
  const k = n * 4;
  const dx = (c[k + 1]! - c[k]!) * (1 - v) + (c[k + 3]! - c[k + 2]!) * v;
  const dy = (c[k + 2]! - c[k]!) * (1 - u) + (c[k + 3]! - c[k + 1]!) * u;
  const l = Math.hypot(dx, dy, 1);
  return { x: -dx / l, y: -dy / l, up: 1 / l };
}

/** Centre height of a tile (metres). Outside the map, the nearest tile's. */
export function tileHeight(zone: HeightField | GameMap, i: number, j: number): number {
  const f = fieldOf(zone);
  return f.ground[tileIndex(f, i, j)[2]]!;
}

/**
 * What lies across one side of the tile holding a point: flat, slope, step, cliff, stairs, ramp,
 * shore or falls. The map's outer edge is flat.
 */
export function edgeKind(zone: HeightField | GameMap, x: number, y: number, dir: Facing): EdgeKind {
  const f = fieldOf(zone);
  const [i, j, n] = tileIndex(f, x, y);
  const [di, dj] = STEP[dir];
  if (i + di < 0 || j + dj < 0 || i + di >= f.w || j + dj >= f.h) return 'flat';
  const code =
    dir === 'e' ? f.edgeE[n]! : dir === 's' ? f.edgeS[n]! : dir === 'w' ? f.edgeE[n - 1]! : f.edgeS[n - f.w]!;
  return EDGE_KINDS[code]!;
}

export interface WaterInfo {
  readonly surface: number;
  readonly bed: number;
  readonly depth: number;
  readonly kind: WaterKind;
  /** The bank, for water that touches land. */
  readonly shore: ShoreKind | null;
  /** A pier or bridge over it. */
  readonly deck: DeckKind | null;
}

/** The water at a point, or null where it is dry. */
export function waterAt(zone: HeightField | GameMap, x: number, y: number): WaterInfo | null {
  const f = fieldOf(zone);
  const n = tileIndex(f, x, y)[2];
  const s = f.water[n]!;
  if (Number.isNaN(s)) return null;
  return {
    surface: s,
    bed: f.bed[n]!,
    depth: s - f.bed[n]!,
    kind: WATER_KINDS[f.waterKind[n]! - 1]!,
    shore: f.shore[n] ? SHORE_KINDS[f.shore[n]! - 1]! : null,
    deck: f.deck[n] ? DECK_KINDS[f.deck[n]! - 1]! : null,
  };
}

/** The flight of stairs or ramp at a point, if any. */
export function flightAt(zone: HeightField | GameMap, x: number, y: number): StairFlight | null {
  const f = fieldOf(zone);
  const k = f.flight[tileIndex(f, x, y)[2]]!;
  return k ? f.flights[k - 1]! : null;
}

/**
 * Where something standing at a point rests (metres): the terrain, plus what the rules stand it on
 * above the ground (a boulder, a roof). A terrace is already in the terrain, so its rule height is
 * not added twice. Add the entity's own altitude (`z`, pixels) × PX_METRES on top.
 */
export function standHeight(f: HeightField, m: GameMap, x: number, y: number): number {
  const t = tileAt(m, Math.floor(x), Math.floor(y));
  // On a roof of a map with levels: the rules' own height (its floor's level plus the roof), as the
  // roof is drawn over its floor, not over the terrain smoothed round the footprint.
  if (t === Tile.Building && hasLevels(m)) {
    const s = standAt(m, x, y);
    if (s !== null) return s * PX_METRES;
  }
  const lift = t === Tile.Terrace ? 0 : (surfaceAt(m, x, y) ?? 0);
  return heightAt(f, x, y) + lift * PX_METRES;
}
